import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, Ban, UserCheck, Search, Filter, Crown, Shield,
  EyeOff, Eye, TrendingUp, TrendingDown, Plus, X, AlertTriangle, Trash2, FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersTab = ({ 
  users, 
  winners,
  token, 
  onRefresh 
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [pointsInput, setPointsInput] = useState({});
  const [showBanForm, setShowBanForm] = useState(false);
  const [banUsername, setBanUsername] = useState('');
  const [banReason, setBanReason] = useState('');
  const [bannedUsernames, setBannedUsernames] = useState([]);
  const [loadingBan, setLoadingBan] = useState(false);
  
  // Test user creation state
  const [showTestUserForm, setShowTestUserForm] = useState(false);
  const [testUsername, setTestUsername] = useState('');
  const [testDisplayName, setTestDisplayName] = useState('');
  const [creatingTestUser, setCreatingTestUser] = useState(false);
  const [creatingBulkUsers, setCreatingBulkUsers] = useState(false);

  // Fetch banned usernames
  useEffect(() => {
    fetchBannedUsernames();
  }, [token]);

  const fetchBannedUsernames = async () => {
    try {
      const res = await axios.get(`${API}/admin/banned-usernames`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBannedUsernames(res.data || []);
    } catch (error) {
      console.error('Failed to fetch banned usernames:', error);
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];
    const safeWinners = Array.isArray(winners) ? winners : [];
    return safeUsers.filter(u => {
      const matchesSearch = search === '' || 
        u.twitch_username?.toLowerCase().includes(search.toLowerCase()) ||
        u.display_name?.toLowerCase().includes(search.toLowerCase());
      
      const matchesFilter = 
        filter === 'all' ||
        (filter === 'active' && !u.is_banned) ||
        (filter === 'banned' && u.is_banned) ||
        (filter === 'winners' && safeWinners.some(w => w.user_id === u.id)) ||
        (filter === 'admins' && u.is_admin) ||
        (filter === 'test' && u.twitch_id?.startsWith('test_'));
      
      return matchesSearch && matchesFilter;
    });
  }, [users, search, filter, winners]);

  const handleBanUser = async (userId, ban) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, 
        { is_banned: ban },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(ban ? 'User banned' : 'User unbanned');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleLeaderboard = async (userId, hide) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, 
        { hide_from_leaderboard: hide },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(hide ? 'Hidden from leaderboard' : 'Shown on leaderboard');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleAdjustPoints = async (userId, adjustment) => {
    const points = parseInt(adjustment);
    if (isNaN(points)) {
      toast.error('Please enter a valid number');
      return;
    }
    try {
      await axios.patch(`${API}/admin/users/${userId}`, 
        { bonus_points: points },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Points adjusted by ${points > 0 ? '+' : ''}${points}`);
      setPointsInput({ ...pointsInput, [userId]: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to adjust points');
    }
  };

  const handleBanTwitchUsername = async () => {
    if (!banUsername.trim()) {
      toast.error('Please enter a Twitch username');
      return;
    }
    setLoadingBan(true);
    try {
      await axios.post(`${API}/admin/ban-twitch-username`, 
        { twitch_username: banUsername.trim(), reason: banReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`User "${banUsername}" has been banned`);
      setBanUsername('');
      setBanReason('');
      setShowBanForm(false);
      fetchBannedUsernames();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban username');
    } finally {
      setLoadingBan(false);
    }
  };

  const handleUnbanUsername = async (username) => {
    try {
      await axios.delete(`${API}/admin/ban-twitch-username/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User "${username}" has been unbanned`);
      fetchBannedUsernames();
      onRefresh();
    } catch (error) {
      toast.error('Failed to unban username');
    }
  };

  // Test user creation functions
  const handleCreateTestUser = async () => {
    if (!testUsername.trim()) {
      toast.error('Please enter a username');
      return;
    }
    setCreatingTestUser(true);
    try {
      await axios.post(`${API}/admin/users/create-test`, {
        username: testUsername.trim(),
        display_name: testDisplayName.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test user "${testUsername}" created!`);
      setTestUsername('');
      setTestDisplayName('');
      setShowTestUserForm(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test user');
    } finally {
      setCreatingTestUser(false);
    }
  };

  const handleCreateBulkTestUsers = async (count) => {
    setCreatingBulkUsers(true);
    try {
      const res = await axios.post(`${API}/admin/users/create-test-bulk?count=${count}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      onRefresh();
    } catch (error) {
      toast.error('Failed to create test users');
    } finally {
      setCreatingBulkUsers(false);
    }
  };

  const handleDeleteTestUser = async (userId, username) => {
    if (!window.confirm(`Delete test user "${username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test user deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getUserWins = (userId) => winners.filter(w => w.user_id === userId).length;
  
  // Check if user is a test user
  const isTestUser = (user) => user.twitch_id?.startsWith('test_');

  return (
    <div className="space-y-4">
      {/* Create Test Users Section */}
      <Card className="glass-card border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <h3 className="font-bold text-blue-400">Test Users</h3>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCreateBulkTestUsers(5)}
                disabled={creatingBulkUsers}
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
              >
                {creatingBulkUsers ? 'Creating...' : '+ Add 5 Random'}
              </Button>
              <Button
                size="sm"
                variant={showTestUserForm ? "outline" : "default"}
                onClick={() => setShowTestUserForm(!showTestUserForm)}
                className={showTestUserForm ? "" : "bg-blue-600 hover:bg-blue-700"}
              >
                {showTestUserForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                {showTestUserForm ? 'Cancel' : 'Create Custom'}
              </Button>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mb-3">
            Create test users for testing submissions, winners, and moderator features. Test users can be deleted.
          </p>
          
          {showTestUserForm && (
            <div className="flex flex-col sm:flex-row gap-2 p-3 bg-black/30 rounded-lg border border-blue-500/30">
              <Input
                placeholder="Username (required)"
                value={testUsername}
                onChange={(e) => setTestUsername(e.target.value)}
                className="flex-1 bg-zinc-900 border-zinc-800"
              />
              <Input
                placeholder="Display name (optional)"
                value={testDisplayName}
                onChange={(e) => setTestDisplayName(e.target.value)}
                className="flex-1 bg-zinc-900 border-zinc-800"
              />
              <Button
                onClick={handleCreateTestUser}
                disabled={creatingTestUser || !testUsername.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creatingTestUser ? 'Creating...' : 'Create'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ban Twitch Username Section */}
      <Card className="glass-card border-red-500/30 bg-red-500/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-bold text-red-400">Pre-Ban Twitch Users</h3>
            </div>
            <Button
              size="sm"
              variant={showBanForm ? "outline" : "destructive"}
              onClick={() => setShowBanForm(!showBanForm)}
            >
              {showBanForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {showBanForm ? 'Cancel' : 'Ban Username'}
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 mb-3">
            Ban users by their Twitch username. If they've logged in before, their permanent Twitch ID is captured. 
            Pre-banned usernames will have their ID captured on first login attempt.
          </p>
          
          {showBanForm && (
            <div className="flex flex-col sm:flex-row gap-2 mb-4 p-3 bg-black/30 rounded-lg border border-red-500/30">
              <Input
                placeholder="Twitch username..."
                value={banUsername}
                onChange={(e) => setBanUsername(e.target.value)}
                className="flex-1 bg-zinc-900 border-zinc-800"
                data-testid="ban-username-input"
              />
              <Input
                placeholder="Reason (optional)..."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="flex-1 bg-zinc-900 border-zinc-800"
              />
              <Button 
                variant="destructive" 
                onClick={handleBanTwitchUsername}
                disabled={loadingBan || !banUsername.trim()}
              >
                <Ban className="w-4 h-4 mr-1" />
                {loadingBan ? 'Banning...' : 'Ban'}
              </Button>
            </div>
          )}
          
          {/* Banned usernames list */}
          {bannedUsernames.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 font-medium">Banned users ({bannedUsernames.length}):</p>
              <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto">
                {bannedUsernames.map((banned, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg text-sm"
                    title={`${banned.twitch_id ? `Twitch ID: ${banned.twitch_id}` : 'Pre-banned (ID captured on login attempt)'}\n${banned.reason ? `Reason: ${banned.reason}` : ''}\nBanned by: ${banned.banned_by || 'Admin'}`}
                  >
                    <Ban className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <div className="flex flex-col">
                      <span className="text-red-300">@{banned.display_name || banned.twitch_username}</span>
                      {banned.twitch_id ? (
                        <span className="text-[10px] text-gray-500">ID: {banned.twitch_id}</span>
                      ) : (
                        <span className="text-[10px] text-yellow-500">Pre-ban (awaiting ID)</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnbanUsername(banned.twitch_id || banned.twitch_username)}
                      className="text-gray-500 hover:text-green-400 transition-colors ml-1"
                      title="Remove from ban list"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
          />
        </div>
        
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-[180px] bg-zinc-900 border-zinc-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
            <SelectItem value="winners">Winners</SelectItem>
            <SelectItem value="admins">Admins</SelectItem>
            <SelectItem value="test">Test Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        Showing {filteredUsers.length} of {users.length} users
      </p>

      {/* Users Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredUsers.map((user) => {
          const winsCount = getUserWins(user.id);
          
          return (
            <Card 
              key={user.id} 
              className={`glass-card ${user.is_banned ? 'border-red-500/50 opacity-60' : ''}`}
            >
              <CardContent className="p-4">
                {/* User Header */}
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src={user.profile_image_url || 'https://via.placeholder.com/40'} 
                    alt={user.display_name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white truncate">{user.display_name}</p>
                      {isTestUser(user) && (
                        <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded flex items-center gap-0.5">
                          <FlaskConical className="w-2.5 h-2.5" /> TEST
                        </span>
                      )}
                      {user.is_admin && <Shield className="w-4 h-4 text-yellow-500" />}
                      {user.is_banned && <Ban className="w-4 h-4 text-red-500" />}
                    </div>
                    <p className="text-xs text-gray-500">@{user.twitch_username}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                  <div className="bg-zinc-900/50 rounded-lg p-2">
                    <p className="text-lg font-bold text-white">{user.participation_count || 0}</p>
                    <p className="text-[10px] text-gray-500">Submissions</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-2">
                    <p className="text-lg font-bold text-yellow-500">{winsCount}</p>
                    <p className="text-[10px] text-gray-500">Wins</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-2">
                    <p className={`text-lg font-bold ${user.bonus_points >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {user.bonus_points > 0 ? '+' : ''}{user.bonus_points || 0}
                    </p>
                    <p className="text-[10px] text-gray-500">Bonus Pts</p>
                  </div>
                </div>

                {/* Points Adjustment */}
                <div className="flex gap-2 mb-3">
                  <Input
                    type="number"
                    placeholder="±pts"
                    value={pointsInput[user.id] || ''}
                    onChange={(e) => setPointsInput({ ...pointsInput, [user.id]: e.target.value })}
                    className="flex-1 bg-zinc-900 border-zinc-800 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdjustPoints(user.id, pointsInput[user.id])}
                    disabled={!pointsInput[user.id]}
                  >
                    <TrendingUp className="w-3 h-3" />
                  </Button>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => handleToggleLeaderboard(user.id, !user.hide_from_leaderboard)}
                  >
                    {user.hide_from_leaderboard ? (
                      <><Eye className="w-3 h-3 mr-1" /> Show</>
                    ) : (
                      <><EyeOff className="w-3 h-3 mr-1" /> Hide</>
                    )}
                  </Button>
                  
                  <Button
                    size="sm"
                    variant={user.is_banned ? "default" : "destructive"}
                    className="flex-1 text-xs"
                    onClick={() => handleBanUser(user.id, !user.is_banned)}
                  >
                    {user.is_banned ? (
                      <><UserCheck className="w-3 h-3 mr-1" /> Unban</>
                    ) : (
                      <><Ban className="w-3 h-3 mr-1" /> Ban</>
                    )}
                  </Button>
                  
                  {isTestUser(user) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      onClick={() => handleDeleteTestUser(user.id, user.display_name)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <Card className="glass-card p-8 text-center">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No users found</p>
        </Card>
      )}
    </div>
  );
};

export default UsersTab;
